import type { BadgeVariant } from '@/components/ui/badge';
import { PROFORMA_SIGNED_DOCUMENT_TYPE_CODE } from '@/services/apis/mutations/purchase-orders/upload-signed-proforma-document';
import { PROFORMA_UNSIGNED_DOCUMENT_TYPE_CODE } from '@/services/apis/mutations/purchase-orders/upload-unsigned-proforma-document';
import type { PurchaseOrderStatusCode } from '@/types/core/purchase-order/purchase-order';

export type PurchaseOrderDocumentUploadBadgeIcon = 'pencil' | 'upload';

export type PurchaseOrderDocumentUploadBadgeVisualConfig = {
  variant: BadgeVariant;
  dotColorClass: string;
  icon: PurchaseOrderDocumentUploadBadgeIcon;
};

export type PurchaseOrderDocumentUploadBadgeConfig = {
  id: string;
  labelKey: string;
  dialogTitleKey?: string;
  dialogTitleWhenConcernedByProductionDeclarationKey?: string;
  documentTypeCode: string;
  includeMailFields?: boolean;
  includeMailFieldsWhenConcernedByProductionDeclaration?: boolean;
  requiresApprovedProductionDeclaration?: boolean;
  visual: PurchaseOrderDocumentUploadBadgeVisualConfig;
  initialUploadStatusCode?: PurchaseOrderStatusCode;
  existingDocumentVisual?: PurchaseOrderDocumentUploadBadgeVisualConfig;
  allowedStatusCodes: PurchaseOrderStatusCode[];
  nextStatusByCurrentStatus?: Partial<
    Record<PurchaseOrderStatusCode, PurchaseOrderStatusCode>
  >;
};

export const PURCHASE_ORDER_DOCUMENT_UPLOAD_BADGES: PurchaseOrderDocumentUploadBadgeConfig[] =
  [
    {
      id: 'unsigned-proforma',
      labelKey: 'documentUploadBadges.unsignedProforma',
      documentTypeCode: PROFORMA_UNSIGNED_DOCUMENT_TYPE_CODE,
      includeMailFieldsWhenConcernedByProductionDeclaration: true,
      visual: {
        variant: 'orange',
        dotColorClass: 'bg-orange-500 dark:bg-orange-400',
        icon: 'upload',
      },
      initialUploadStatusCode: 'WAITING_FOR_SUPPLIER_AGREEMENT',
      existingDocumentVisual: {
        variant: 'green',
        dotColorClass: 'bg-green-600 dark:bg-green-400',
        icon: 'pencil',
      },
      allowedStatusCodes: [
        'WAITING_FOR_SUPPLIER_AGREEMENT',
        'SUPPLIER_AGREEMENT_RECEIVED',
        'IN_PRODUCTION',
        'IN_ADJUSTMENTS',
      ],
      dialogTitleKey: 'documentUploadBadges.unsignedProformaDialogTitle',
      dialogTitleWhenConcernedByProductionDeclarationKey:
        'documentUploadBadges.unsignedProformaEudrDialogTitle',
      nextStatusByCurrentStatus: {
        WAITING_FOR_SUPPLIER_AGREEMENT: 'SUPPLIER_AGREEMENT_RECEIVED',
        SUPPLIER_AGREEMENT_RECEIVED: 'IN_ADJUSTMENTS',
        IN_ADJUSTMENTS: 'IN_ADJUSTMENTS',
        IN_PRODUCTION: 'IN_ADJUSTMENTS',
      },
    },
    {
      id: 'signed-proforma',
      labelKey: 'documentUploadBadges.signedProforma',
      documentTypeCode: PROFORMA_SIGNED_DOCUMENT_TYPE_CODE,
      includeMailFields: true,
      requiresApprovedProductionDeclaration: true,
      visual: {
        variant: 'orange',
        dotColorClass: 'bg-orange-500 dark:bg-orange-400',
        icon: 'upload',
      },
      allowedStatusCodes: ['SUPPLIER_AGREEMENT_RECEIVED', 'IN_ADJUSTMENTS'],
      dialogTitleKey: 'documentUploadBadges.signedProformaDialogTitle',
      nextStatusByCurrentStatus: {
        SUPPLIER_AGREEMENT_RECEIVED: 'IN_PRODUCTION',
        IN_ADJUSTMENTS: 'IN_PRODUCTION',
      },
    },
  ];
